import { CUSTOM_FORMATS } from './formats.js';
import { isString } from '../util/validators.js';


const DEFAULT_LANGUAGE = 'en-us';
const DEFAULT_TRANSLATION = 'Translation not found';


/**
 *  Language - module for dealing with i18n translations
 *
 *  @param {Constructor} Formatter - class to format translation with
 *  @param {String} language - language to translate to
 *  @param {Object} translationMapping - mapping of key paths to translations to feed parameters into
 *  @constructor
 */
export function Language(Formatter, language = DEFAULT_LANGUAGE, translationMapping = {}) {
    this.Formatter = Formatter;
    this.language = language;
    this.idleQueue = [];
    this.translations = {};
    this.formatters = {};
    this.defaultLeaf = new this.Formatter(DEFAULT_TRANSLATION, this.language, CUSTOM_FORMATS);
    this.parseNode(translationMapping);
    this.beginIdle(this.translations);
}

/**
 *  mergeWith - merges another Language instance into the current instance
 *
 *  @param {Language} other - other language instance
 */
Language.prototype.mergeWith = function(other) {
    other.stopIdle();
    Object.entries(other.translations).forEach(([ key, val ]) => {
        this.translations[key] = val;
    });
    Object.entries(other.formatters).forEach(([ key, val ]) => {
        this.formatters[key] = val;
    });
    this.beginIdle(other.translations);
};

/**
 *  find - finds the leaf in the translation map given a pathway
 *
 *  @param {String} i18nKey - pathway delimited by '.'
 *  @return {Formatter} - leaf in translations
 */
Language.prototype.find = function(i18nKey) {
    const formatter = this.formatters[i18nKey];
    if (formatter) {
        return formatter;
    }
    const template = this.translations[i18nKey];
    if (isString(template)) {
        const evaluatedFormatter = new this.Formatter(template, this.lang, CUSTOM_FORMATS);
        this.formatters[i18nKey] = evaluatedFormatter;
        return evaluatedFormatter;
    }
    return this.defaultLeaf;
};

/**
 *  translate - i18n translation
 *
 *  @param {String} i18nKey - pathway delimited by '.'
 *  @param {Object} parameters - values to pass to the leaf formatting function
 *  @return {String} - final translation
 *  @throws - if parameters don't meet the Formatter expectations, extra params are OK
 */
Language.prototype.translate = function(i18nKey, parameters = {}) {
    const translation = this.find(i18nKey);
    if (translation === this.defaultLeaf) {
        // eslint-disable-next-line no-console
        console.warn(`(${i18nKey}) not found`);
    }
    return translation.format(parameters);
};

/**
 *  parseNode - helper function for mapping a string tree to a translation tree
 *
 *  @param {Object|String} node - possible encounter in the graph - either object (subtree) or string leaf
 *  @param {String} path - accumulated path to the node
 */
Language.prototype.parseNode = function(node, path = '') {
    if (isString(node)) {
        this.translations[path] = node;
    }
    else {
        Object
            .entries(node)
            .filter(([ , value ]) => value || isString(value))
            .forEach(([ key, value ]) => {
                this.parseNode(value, path ? `${path}.${key}` : key);
            });
    }
};

/**
 * @param {Object} translations - The translations that need formatters
 * @returns {Boolean} True if idleCallbacks are supported, false otherwise
 */
Language.prototype.beginIdle = function(translations) {
    if (!window.requestIdleCallback) {
        return false;
    }

    this.idleQueue = this.idleQueue.concat(Object.keys(translations));

    this.stopIdle();
    this._idleId = window.requestIdleCallback(this._idleHandler.bind(this));
    return true;
};

/**
 * Stop the running of the idleQueue processor
 * @returns {Boolean} True if idleCallbacks are supported, false otherwise
 */
Language.prototype.stopIdle = function() {
    if (!window.cancelIdleCallback) {
        return false;
    }
    window.cancelIdleCallback(this._idleId);
    return true;
};

Language.prototype._idleHandler = function(deadline) {
    if (this.idleQueue.length === 0) {
        return;
    }
    if (deadline && deadline.timeRemaining() >= 2) {
        const shortLang = this.language.substring(0, 2);
        let shouldRunAgain = true;
        while (shouldRunAgain) {
            const i18nKey = this.idleQueue.pop();
            if (!this.formatters[i18nKey] && isString(this.translations[i18nKey]) && i18nKey) {
                this.formatters[i18nKey] = new this.Formatter(this.translations[i18nKey], shortLang, CUSTOM_FORMATS);
                shouldRunAgain = false;
            }
            if (this.idleQueue.length === 0) {
                shouldRunAgain = false;
            }
        }
    }
    this._idleId = window.requestIdleCallback(this._idleHandler.bind(this));
};
